import { type ComponentType, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useIsSmartEdit } from "~/app/config/pageMetadataContext";
import { MarketingConsent } from "~/features/marketingConsent/components/MarketingConsent";
import type ComponentTypes from "~/features/cms/componentTypes";
import escapeRegExp from "lodash/escapeRegExp";
import { userHasMarkettingPermission, userIsPublic } from "~/domains/user/utils/roles";
import { sendMarketingConsent } from "~/features/cms/api/giveMarketingConsent";
import Modal, { ModalTitle } from "~/common/components/Modal";
import { Box, Button, type SvgIconProps } from "@schaeffler-commerce/design-system";
import { Alert } from "@schaeffler-commerce/design-system";
import CloseIcon from "@mui/icons-material/Close";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import WbIncandescentIcon from "@mui/icons-material/WbIncandescent";
import { ScreenNews as ScreenNewsIcon } from "@schaeffler-commerce/design-system";
import { useDispatch } from "react-redux";
import { openAuthOverlay } from "~/features/registration/components/redux/AuthActions";

export interface CMSNewsletterConsentDTO {
  type: ComponentTypes.CMSNewsLetterConsent;
  className?: string;
  title: string;
  subtitle?: string;
  reasons?: {
    icon: string;
    label: string;
  }[];
}

type Props = Omit<CMSNewsletterConsentDTO, "type">;

export const CMSNewsletterConsent = ({ title, subtitle, reasons }: Props) => {
  const { t } = useTranslation();
  const isInSmartEdit = useIsSmartEdit();
  const [consent, setConsent] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const [successModal, setSuccessModal] = useState(false);
  const [error, setError] = useState("");
  const dispatch = useDispatch();

  const { titleProps, subtitleProps, reasonProps } = useMemo(() => {
    return {
      titleProps: transformText(title),
      subtitleProps: transformText(subtitle),
      reasonProps: reasons?.map(transformReasonDTO),
    };
  }, [title, subtitle, reasons]);

  // If the user already has a newsletter-xx group then the component should not render
  if (userHasMarkettingPermission() && !isInSmartEdit) {
    return null;
  }

  const handleSelected = async () => {
    if (isInSmartEdit) {
      return;
    }
    if (userIsPublic()) {
      dispatch(openAuthOverlay("register"));
      return;
    }
    try {
      setInFlight(true);
      await sendMarketingConsent(consent);
      setSuccessModal(true);
    } catch (e) {
      console.error(e);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setError(e as any);
    } finally {
      setInFlight(false);
    }
  };

  const onClosePopup = () => window.location.reload();

  return (
    <>
      <MarketingConsent.Card>
        {titleProps && <MarketingConsent.Title {...titleProps} />}
        <MarketingConsent.ConsentCheckbox selected={consent} setSelected={setConsent} disabled={inFlight} />
        <Button size="medium" onClick={handleSelected} sx={{ mt: 1 }} loading={inFlight} disabled={!consent}>
          {t("page.completeRegistration.submit")}
        </Button>
        {subtitleProps && <MarketingConsent.Title sx={{ mt: 4 }} {...subtitleProps} />}
        {error && (
          <Alert className={"my-2"} severity={"error"}>
            {t(error)}
          </Alert>
        )}
        {reasonProps && reasonProps.length > 0 && <MarketingConsent.Reasons reasons={reasonProps} />}
      </MarketingConsent.Card>
      {successModal && (
        <Modal onClose={onClosePopup} footerChildren={<Button onClick={onClosePopup}>{t("close")}</Button>}>
          <Box sx={{ mb: 4 }}>
            <ModalTitle>{t("cms.newsletterConsent.thankYou")}</ModalTitle>
          </Box>
        </Modal>
      )}
    </>
  );
};

const FUNCTION_PREFIX = '#{t("';
const FUNCTION_SUFFIX = '")}';
const FUNCTION_REGEXP = new RegExp(`^${escapeRegExp(FUNCTION_PREFIX)}(.*)${escapeRegExp(FUNCTION_SUFFIX)}$`);
const REASON_ICON_MAP: Record<string, ComponentType<SvgIconProps>> = {
  Cup: EmojiEventsIcon,
  ScreenNews: ScreenNewsIcon,
  PriceTag: LocalOfferIcon,
  Invitation: LocalActivityIcon,
  Lightbulb: WbIncandescentIcon,
};

function transformText(text?: string) {
  if (!text) {
    return null;
  }
  text = text.trim();
  // If matches function regexp, extract the key
  const match = text.match(FUNCTION_REGEXP);
  if (match) {
    return {
      children: <Trans i18nKey={match[1]} />,
    };
  }

  return {
    dangerouslySetInnerHTML: { __html: text },
  };
}

function transformReasonDTO(reason: { icon: string; label: string }) {
  const data = transformText(reason.label);
  const icon = REASON_ICON_MAP[reason.icon] ?? CloseIcon;
  return {
    icon,
    children: data?.dangerouslySetInnerHTML ? <div {...data} /> : data?.children,
  };
}
